import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useScrollToTop } from '../hooks/useScrollToTop';

const PrivacyPage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Destress Daily</title>
        <meta name="description" content="Privacy Policy for Destress Daily - Learn how we collect, use, and protect your personal information on our stress relief and wellness platform." />
        <meta name="keywords" content="privacy policy, destress daily, data protection, personal information" />
        <meta property="og:title" content="Privacy Policy - Destress Daily" />
        <meta property="og:description" content="Privacy Policy for Destress Daily - Learn how we collect, use, and protect your personal information." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dailyhealthhacks.com/privacy" />
        <link rel="canonical" href="https://dailyhealthhacks.com/privacy" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-gradient rounded-2xl mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Privacy Policy
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Your privacy is important to us. Learn how we collect, use, and protect your information.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
              
              <div className="mb-8">
                <p className="text-sm text-gray-500 mb-4">
                  <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                  <p className="text-gray-600 mb-4">
                    Destress Daily ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
                  </p>
                  <p className="text-gray-600 mb-4">
                    By using our website, you consent to the data practices described in this policy.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information</h3>
                  <p className="text-gray-600 mb-4">
                    We may collect personal information that you voluntarily provide to us, including:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Email address (when you subscribe to our newsletter)</li>
                    <li>Name (when you contact us)</li>
                    <li>Message content (when you send us feedback or questions)</li>
                    <li>Any other information you choose to provide</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Automatically Collected Information</h3>
                  <p className="text-gray-600 mb-4">
                    When you visit our website, we may automatically collect certain information, including:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Basic analytics data (pages visited, time spent)</li>
                    <li>Browser type and version</li>
                    <li>Device information</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                  <p className="text-gray-600 mb-4">
                    We use the information we collect for the following purposes:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>To provide and maintain our website and services</li>
                    <li>To send you stress relief tips and wellness content via email (with your consent)</li>
                    <li>To respond to your inquiries and provide customer support</li>
                    <li>To improve our website and user experience</li>
                    <li>To analyze website usage and trends</li>
                    <li>To ensure the security and integrity of our website</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Newsletter and Email Communications</h2>
                  <p className="text-gray-600 mb-4">
                    When you subscribe to our newsletter, we collect your email address to send you stress relief tips and wellness content. You can unsubscribe at any time by:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Clicking the unsubscribe link in any email we send</li>
                    <li>Contacting us directly at hello@destressdaily.com</li>
                    <li>Using the unsubscribe form on our website</li>
                  </ul>
                  <p className="text-gray-600 mb-4">
                    We will not share your email address with third parties for marketing purposes without your explicit consent.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                  <p className="text-gray-600 mb-4">
                    We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Secure hosting and infrastructure</li>
                    <li>Encryption of data in transit</li>
                    <li>Access controls and authentication measures</li>
                    <li>Regular security assessments and updates</li>
                  </ul>
                  <p className="text-gray-600 mb-4">
                    However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
                  <p className="text-gray-600 mb-4">
                    We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
                  </p>
                  <p className="text-gray-600 mb-4">
                    When you unsubscribe from our newsletter, we will remove your email address from our mailing list within 30 days.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights and Choices</h2>
                  <p className="text-gray-600 mb-4">
                    Depending on your location, you may have certain rights regarding your personal information, including:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>The right to access your personal information</li>
                    <li>The right to correct inaccurate information</li>
                    <li>The right to delete your personal information</li>
                    <li>The right to restrict processing of your information</li>
                    <li>The right to data portability</li>
                    <li>The right to object to processing</li>
                  </ul>
                  <p className="text-gray-600 mb-4">
                    To exercise these rights, please contact us at hello@destressdaily.com.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
                  <p className="text-gray-600 mb-4">
                    Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Privacy Policy</h2>
                  <p className="text-gray-600 mb-4">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                  <p className="text-gray-600 mb-4">
                    We encourage you to review this Privacy Policy periodically for any changes.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
                  <p className="text-gray-600 mb-4">
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-600">
                      <strong>Email:</strong> hello@destressdaily.com<br />
                      <strong>Website:</strong> https://destressdaily.com/contact
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 pt-8 mt-8">
                  <p className="text-sm text-gray-500">
                    By using Destress Daily, you acknowledge that you have read and understood this Privacy Policy and consent to the collection and use of your information as described herein.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage; 